import React from "react";
import {
  Activity,
  AlertOctagon,
  TrendingDown,
  CheckCircle2,
  Gauge,
  Link2,
  TrendingUp,
} from "lucide-react";
import { Sparkline } from "../Sparkline";
import { useInsights } from "../../data/insightsStore";

const cards = [
  { key: "active",              label: "Active transactions",   icon: Activity,      color: "#3B82F6" },
  { key: "highRisk",            label: "High risk deals",       icon: AlertOctagon,  color: "#F43F5E" },
  { key: "predictedFallThrough", label: "Predicted fall-through",icon: TrendingDown,  color: "#6366F1" },
  { key: "exchangeForecast",    label: "Exchange forecast",     icon: CheckCircle2,  color: "#6366F1" },
  { key: "avgRiskScore",        label: "Avg risk score",        icon: Gauge,         color: "#F59E0B" },
  { key: "chainStability",      label: "Chain stability",       icon: Link2,         color: "#6366F1" },
  { key: "weeklyTrend",         label: "Weekly trend",          icon: TrendingUp,    color: "#6366F1" },
];

const empty = { value: "—", trend: "0", spark: [0] };

export const RiskKPIHeader = () => {
  const { data } = useInsights();
  const kpis = data?.kpis || {};
  const dealCount = data?.deal_count || 0;
  const dirty = dealCount === 0;
  return (
    <header className="sticky top-0 z-30 glass-light border-b cf-divider-soft" data-testid="risk-kpi-header">
      <div className="px-4 md:px-10 pt-6 md:pt-9 pb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-indigo-700 mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 cf-pulse" />
            Intelligence centre
          </span>
          <h1 className="cf-h-page" data-testid="risk-title">AI Risk &amp; Chain Intelligence</h1>
          <p className="text-[13px] text-slate-500 mt-2">
            Predictive intelligence across <span className="text-slate-700 font-medium" data-testid="insights-deal-count">{dealCount} active transaction{dealCount === 1 ? "" : "s"}</span>
            {data?.director?.office_pipelines?.length ? `, ${data.director.office_pipelines.length} office${data.director.office_pipelines.length === 1 ? "" : "s"}` : ""} and live participant signals
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-700 text-[11.5px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 cf-pulse" />
            Live · model v3.4
          </div>
          <div className="flex items-center gap-0.5 bg-white border border-[color:var(--cf-border)] rounded-xl p-1">
            {["1d", "7d", "30d", "90d", "YTD"].map((p, i) => (
              <button
                key={p}
                data-testid={`window-${p}`}
                className={`h-7 px-3 rounded-lg text-[12px] font-medium transition-all ${
                  i === 1 ? "bg-slate-900 text-white shadow-[0_4px_10px_-2px_rgba(15,23,42,0.30)]" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <button data-testid="risk-export-btn" className="btn btn-dark">Export brief</button>
        </div>
      </div>
      <div className="px-4 md:px-10 pb-5 md:pb-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 cf-stagger">
        {cards.map((c) => {
          const k = kpis[c.key] || empty;
          const Icon = c.icon;
          const danger = c.key === "highRisk" || c.key === "avgRiskScore";
          const trend = String(k.trend ?? "0");
          return (
            <div
              key={c.key}
              data-testid={`risk-kpi-${c.key}`}
              className={`cf-kpi ${dirty ? "opacity-60" : ""}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-7 h-7 rounded-lg bg-slate-50 border border-[color:var(--cf-border-soft)] flex items-center justify-center">
                  <Icon size={13} className="text-slate-600" strokeWidth={1.8} />
                </div>
                <span className={`text-[11px] font-semibold cf-num ${
                  trend.startsWith("-") ? (danger ? "cf-delta-pos" : "cf-delta-neg") : (danger ? "cf-delta-neg" : "cf-delta-pos")
                }`}>
                  {trend}
                </span>
              </div>
              <p className="font-display text-[24px] font-bold text-slate-900 leading-none mt-5 tracking-[-0.025em]">{String(k.value)}</p>
              <p className="text-[11.5px] text-slate-500 mt-1.5 leading-tight">{c.label}</p>
              <div className="mt-3 -mb-1 opacity-90">
                <Sparkline data={k.spark || [0]} width={140} height={26} color={c.color} />
              </div>
            </div>
          );
        })}
      </div>
    </header>
  );
};
